import Plyr from 'plyr';

/**
 * VideoAutoplayObserver
 * ---------------------
 * Initialise Plyr video players (native <video> or embeds like YouTube)
 * and control autoplay / pause behaviour with an Intersection Observer.
 *
 * – Only the outer wrapper `.twz-element-video--player` is observed, so it
 *   always has dimensions, even before Plyr injects an <iframe>.
 * – Each wrapper stores its Plyr instance on `wrapper._plyr` for quick access.
 *
 * v3 – 13 May 2025
 *   • Fix: wanneer de pagina laadt met een video al volledig in viewport,
 *     speelde hij soms niet omdat `player.play()` vóór `ready` werd
 *     aangeroepen (YouTube‑provider geeft dan geen Promise terug).
 *     ‑ `wrapper._isIntersecting` wordt nu live bijgehouden; zodra Plyr
 *       de ‘ready’‑event vuurt, checken we die vlag en starten we alsnog.
 */
class VideoAutoplayObserver {
  constructor() {
    document.addEventListener('DOMContentLoaded', () => this.init());
  }

  /**
   * Start‑up: zoek alle wrappers, maak Plyr‑instantie en activeer observer
   */
  init() {
    const wrappers = document.querySelectorAll('.twz-element-video--player');
    if (!wrappers.length) return;

    wrappers.forEach(wrapper => {
      let media;
      if(wrapper.tagName === 'VIDEO') {
        media = wrapper;
      } else {
        media = wrapper.querySelector('video, [data-plyr-provider]');
      }

      if (!media) return; // skip incomplete markup

      const player = new Plyr(media, {
        volume: 0,
        muted: true,   // Chrome autoplay policy
        autoplay: false,
        loop: { active: true },
      });

      // Cache
      wrapper._plyr = player;
      wrapper._isIntersecting = false; // default

      // Als de speler klaar is en het element staat al in beeld → play
      player.on('ready', () => {
        if (wrapper._isIntersecting) {
          this.safePlay(player);
        }
      });
    });

    if ('IntersectionObserver' in window) {
      this.setupObserver(wrappers);
    } else {
      this.fallbackPlay(wrappers);
    }
  }

  /**
   * Helper: probeer af te spelen, maar vang cases waar geen Promise terugkomt.
   */
  safePlay(player) {
    const maybePromise = player.play();
    if (maybePromise && typeof maybePromise.catch === 'function') {
      maybePromise.catch(() => {/* autoplay failure – ignore */});
    }
  }

  setupObserver(wrappers) {

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.4, // play when 40 % visible
    };

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {

        const wrapper = entry.target;
        const player = wrapper._plyr;
        if (!player) return;

        // Update zichtbaarheid‑vlag
        wrapper._isIntersecting = entry.isIntersecting;

        if (entry.isIntersecting) {
          this.safePlay(player);
        } else {
          player.pause();
        }
      });
    }, options);

    wrappers.forEach(wrapper => observer.observe(wrapper));
  }

  /**
   * Fallback voor browsers zonder IntersectionObserver – speel direct af.
   */
  fallbackPlay(wrappers) {
    wrappers.forEach(wrapper => {
      const player = wrapper._plyr;
      if (!player) return;
      this.safePlay(player);
    });
  }
}

// Bootstrappen
new VideoAutoplayObserver();